import React, {useEffect, useState} from 'react';
import {Alert, Box, Button, Fade, FormControl, Modal, Stack, Typography} from "@mui/material"
import styles from "./RowEditor.module.scss"
import RowActionsMenu from "./RowActionsMenu"
import AsyncButton from "../button/AsyncButton"
import DatasetRadio from "../dataset/DatasetRadio"
import CellInputGroup from "./CellInputGroup"
import {RowDataset} from "../../api/enum/RowDataset"
import CellJson from "../../api/json/CellJson"
import ApiError from "../../api/ApiError"
import {ColumnType} from "../../api/enum/ColumnType"
import {RowRequests} from "../../api/requests/RowRequests"
import {RowOrigin} from "../../api/enum/RowOrigin"
import {ProjectJson} from "../../api/json/ProjectJson"
import RowJson from "../../api/json/RowJson"

interface CreateRowModalProps {
    open: boolean
    onClose: () => void
    keepMounted?: boolean
    project: ProjectJson
    origin?: RowOrigin
    generativeResultId?: string
    row?: RowJson
    referenceValues?: Record<string, string>
    defaultDataset?: RowDataset
    allowSaveAndNext?: boolean
    didCreate?: (finished: boolean, row: RowJson) => void
    didDelete?: () => void
    didUpdate?: (row: RowJson) => void
}

function CreateRowModal({
                                open,
                                keepMounted,
                                onClose,
                                project,
                                origin,
                                generativeResultId,
                                row,
                                referenceValues,
                                defaultDataset,
                                allowSaveAndNext,
                                didCreate,
                                didDelete,
                                didUpdate,
                            }: CreateRowModalProps) {
    const [cells, setCells] = useState<CellJson[]>(row?.cells ?? [])
    const [dataset, setDataset] = useState<RowDataset>(row?.dataset ?? defaultDataset ?? RowDataset.TRAINING)

    const [unsavedChanges, setUnsavedChanges] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<ApiError>()

    useEffect(() => {
        if (!referenceValues) return

        const defaultCells: CellJson[] = []
        Object.entries(referenceValues).forEach(([key, value]) => {
            defaultCells.push({
                column: project.columns.find(it => it.reference === key)!,
                value: value,
            })
        })
        setCells(defaultCells)
    }, [referenceValues])

    const reset = () => {
        const resetCells = [...cells]
        // Reset everything to empty except the dropdowns
        cells.forEach(cell => {
            cell.value = cell.column.type === ColumnType.PREDEFINED_OPTIONS ? cell.value : ""
        })
        setCells(resetCells)
    }

    const handleSave = (finished: boolean) => {
        setSaving(true)
        if (!row) {
            RowRequests.create(project.id, dataset, origin ?? RowOrigin.MANUAL, cells, generativeResultId ?? null)
                .then(saved => {
                    setError(undefined)
                    didCreate && didCreate(finished, saved)

                    if (!finished) reset()
                })
                .catch(setError)
                .finally(() => setSaving(false))
        } else {
            RowRequests.update(row.id, dataset, cells)
                .then(updated => {
                    setError(undefined)
                    didUpdate && didUpdate(updated)
                    onClose()
                })
                .catch(setError)
                .finally(() => setSaving(false))
        }
    }

    useEffect(() => {
        // TODO: This doesn't work 100%, if you revert your changes it doesn't return to false. Haven't figured it out.
        if (!row) {
            setUnsavedChanges(true)
        } else {
            setUnsavedChanges(row.cells !== cells || row.dataset !== dataset)
        }
    }, [row, cells, dataset])

    const saveAndNextMode = row === undefined && allowSaveAndNext

    return (
        <Modal
            open={open}
            keepMounted={keepMounted}
            onClose={(_, reason) => {
                if (reason === "escapeKeyDown" || !unsavedChanges)
                    onClose()
            }}
        >
            <Fade in={open}>
                <Stack className={styles.modal}>
                    <Stack direction="row"
                           justifyContent="space-between"
                           alignItems="center"
                           className={styles.header}
                    >
                        <Typography variant="h2">{!row ? "Create" : "Edit"} Row</Typography>
                        <RowActionsMenu row={row}
                                            didDelete={() => {
                                                didDelete && didDelete()
                                                onClose()
                                            }}/>
                    </Stack>

                    <Box className={styles.container}>
                        {error &&
                            <Alert sx={{zIndex: 100, mb: 3}}
                                   severity="error">
                                {error.resolveUserMessage()}
                            </Alert>
                        }

                        <Stack spacing={3}>
                            <CellInputGroup
                                cells={cells}
                                columns={project.columns}
                                onChange={(newValue, reference) => {
                                    const newCells = [...cells]
                                    const index = newCells.findIndex(cell => cell.column.reference === reference)
                                    if (index == -1) {
                                        let column = project.columns.find(column => column.reference === reference)!
                                        setCells([
                                            {
                                                column: column,
                                                value: newValue,
                                            },
                                            ...newCells,
                                        ])
                                    } else {
                                        newCells[index].value = newValue
                                        setCells(newCells)
                                    }
                                }
                                }
                            />
                            <FormControl>
                                <Typography variant="subtitle1">Dataset</Typography>
                                <DatasetRadio dataset={dataset} setDataset={setDataset}/>
                            </FormControl>
                        </Stack>
                    </Box>

                    <Stack direction="row"
                           spacing={2}
                           justifyContent="flex-end"
                           className={styles.footer}>
                        <Button onClick={() => {
                            if (row)
                                setCells(row.cells)
                            onClose()
                        }}
                                variant="outlined"
                                color="secondary"
                        >
                            {unsavedChanges ? "Cancel" : "Close"}
                        </Button>
                        <AsyncButton waiting={saving}
                                     disabled={!unsavedChanges}
                                     onClick={() => handleSave(!saveAndNextMode)}>
                            {saveAndNextMode ? "Save & next" : "Save & close"}
                        </AsyncButton>
                    </Stack>
                </Stack>
            </Fade>
        </Modal>
    );
}

export default CreateRowModal;
